import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { styled } from "@mui/styles";
import StewartTable, {
  DetailsViewConfig,
  SelectionConfig,
  StewartTableColumn,
} from "controls/global/stewart-table/StewartTable";
import { EmptyStateType } from "controls/global/stewart-table/StewartTableBodyEmpty";
import StewartTableFooterPaginator from "controls/global/stewart-table/StewartTableFooterPaginator";
import { Order } from "controls/global/stewart-table/stewartTableHelper";
import {
  borderRadius,
  borderSize,
  colors,
  fontSize,
  fontWeight,
  gaps,
  iconSize,
  margin,
  padding,
  zIndex,
} from "theme/defaultStyle";
import useSharedTabStore from "utils/context/InventoryShareContext";
import { useUser } from "utils/context/UserContext";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import { PageType, PERMISSIONS } from "utils/data/enum";
import { InventorySharedSearchCriteria } from "entities/UIModel/InventorySharedSearchCriteria";
import { useConfigContext } from "utils/context/ConfigContextProvider";
import { debounce } from "lodash";
import FileExportDialog, { SELECTION } from "controls/global/file-export-dialog/FileExportDialog";
import IosShareIcon from "@mui/icons-material/IosShare";
import InputSearch from "./InputSearch";
import { InventorySharedUI, ReceivingAgency } from "entities/UIModel/InventorySharedUI";
import { InventorySharedExportCriteria } from "entities/ApiModel/InventorySharedExportCriteria";
import ConfirmationDialog from "controls/global/dialogs/confirmation-dialog/ConfirmationDialog";
import { Choice } from "controls/global/file-export-dialog/FileSelectionOptions";
import { SelectedHeader } from "pages/components/SelectedHeader";

const Container = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.medium1,
  padding: padding.large1,
});

const Toolbar = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  flexWrap: "wrap",
  gap: gaps.medium1,
});

const ToolbarActions = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.medium1,

  "& .MuiButton-root": {
    color: colors.blue15,
    display: "flex",
    gap: gaps.small1,
    paddingBottom: padding.xsmall1,
    paddingLeft: padding.xsmall2,
    paddingRight: padding.xsmall2,
    paddingTop: padding.xsmall1,
    textTransform: "none",
    "&.Mui-disabled": {
      background: colors.white,
      color: colors.blue12,
      opacity: 1,
    },
    "& .MuiSvgIcon-root": {
      fontSize: iconSize.medium,
    },
    "& .rotate-90": {
      transform: "rotate(90deg)",
    },
  },
});

// Width of the row-header (expand/checkbox) column; also fed to StewartTable's
// `rowHeaderWidth` below so the two can't drift apart.
const ROW_HEADER_WIDTH = 100;

const DetailsContainer = styled(Box)({
  padding: padding.medium1,
  // StewartTable renders the details row as a single cell spanning every
  // column with `padding: 0`, so the content would otherwise start at the
  // table's left edge. Indent past the row header — plus the same
  // `padding.medium1` a body cell uses — to line up with the Assigned Agent
  // column. Padding rather than margin keeps the grey panel full-width.
  paddingLeft: `calc(${ROW_HEADER_WIDTH}px + ${padding.medium1})`,
  backgroundColor: colors.grey25,
});

const DetailsRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: gaps.medium1,
  padding: `${padding.xsmall1} 0`,
  // Indent in from the "Sharing Agency / Legacy ID (Location)" header so the
  // rows read as content belonging to it. Must follow the `padding` shorthand
  // above to take effect.
  paddingLeft: padding.xlarge21,
  // Match the detail header (and StewartTable's body cells) rather than
  // rendering as fine print.
  fontSize: fontSize.large,
});

const StyledTableContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.large1,
  "& .MuiOutlinedInput-input": {
    marginBottom: margin.xxsmall2,
    marginLeft: margin.small2,
    marginTop: margin.small,
    padding: `${padding.small} ${padding.small2}`,
  },
  "& table.MuiTable-root": {
    "& th.MuiTableCell-root.MuiTableCell-head": {
      color: colors.grey08,
      fontSize: fontSize.base,
      fontWeight: fontWeight.normal2,
      "& div.MuiBox-root": {
        color: colors.grey08,
        fontSize: fontSize.base,
        fontWeight: fontWeight.normal2,
      },
      "& div.MuiInputBase-root.MuiOutlinedInput-root.MuiOutlinedInput-adornedEnd": {
        border: 0,
        borderBottom: `1px solid ${colors.grey15}`,
        borderRadius: 0,
        "&:focus-within": {
          borderBottom: `2px solid ${colors.blue15}`,
        },
        "& fieldset": {
          border: 0,
        },
      },
    },
    "& thead": {
      "& tr th div.MuiFormControl-root": {
        width: "100%",
        marginLeft: margin.small1_5,
      },
      "& tr th label.MuiFormLabel-root:not(.MuiInputLabel-shrink)": {
        transform: "translate(14px, 11px) scale(1)",
      },
      "& .MuiTableRow-root.header-actions th": {
        paddingRight: padding.large2,
        zIndex :zIndex.positiveMax,
      },
      "& tr th input": {
        padding: "6px 3px",
      },
    },
    "& tbody": {
      "& tr:not(.checked-row):hover td": {
        background: colors.grey10,
      },
      "& tr td.emptyCell": {
        paddingLeft: "20px",
        boxShadow: "none",
      },
      "& td:not(.emptyCell)": {
        maxWidth: "75px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      "& tr:last-child": {
        "& td:not(.emptyCell)": {
          borderBottom: `1px solid ${colors.grey09}`,
        },
        "& td:not(.emptyCell):first-child": {
          borderBottomLeftRadius: borderSize.zero,
        },
        "& td:not(.emptyCell):last-child": {
          borderBottomRightRadius: borderSize.zero,
        },
      },
    },
    "& td.MuiTableCell-root.MuiTableCell-body": {
      "& div": {
        color: colors.grey08,
        fontWeight: fontWeight.normal1,
      },
      "& button.MuiButtonBase-root": {
        margin: `${margin.xxsmall1} ${margin.small1}`,
      },
    },
    "& td.MuiTableCell-root.MuiTableCell-footer": {
      borderBottom: `1px solid ${colors.grey09}`,
      borderBottomLeftRadius: borderRadius.small,
      borderBottomRightRadius: borderRadius.small,
      borderLeft: `1px solid ${colors.grey09}`,
      borderRight: `1px solid ${colors.grey09}`,
      padding: padding.xsmall2,
      paddingRight: padding.small2,
    },
  },
  "& > .MuiTableContainer-root": {
    maxHeight: "100px",
    minHeight: "auto",
  },
});

const NoRecordsMessage = "No shares found. Adjust your filters and try again.";

const EXPORT_AGENCY_SELECTION_CHOICES: Choice<SELECTION>[] = [
  { label: 'Selected agencies only', value: 'SELECTED' },
  { label: 'All Agencies', value: 'ALL' },
];

const InventoryShareShared = () => {
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof InventorySharedUI | undefined>(
    "assignedLocationDisplayName"
  );
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [userInputDebounceTimeMs, setUserInputDebounceTimeMs] =
    useState<number>(750);
  const [settingsLoaded, setSettingsLoaded] = useState<boolean>(false);
  const [data, setData] = useState<InventorySharedUI[]>();
  const [readyToSearch, setReadyToSearch] = useState<boolean>(false);
  const [emptyStateType, setEmptyStateType] = useState<EmptyStateType>(
    EmptyStateType.DEFAULT
  );
  const [filters, setFilters] = useState<InventorySharedSearchCriteria>();
  const [openExportDialog, setOpenExportDialog] = useState<boolean>(false);
  const [shareToDelete, setShareToDelete] = useState<ReceivingAgency | null>(null);

  const { generalConfig } = useConfigContext();
  const [, { getRowsPerPageSetting, setRowsPerPageSetting }] = useUser();
  const [{ profileSettings }] = useProfileSettingsCache();

  const permissionCodes = profileSettings?.userPermissionItemTypes as string[];
  const canDeleteShare = permissionCodes?.includes(PERMISSIONS.INV_SHARE_DELETE);
  const canExportShares = permissionCodes?.includes(PERMISSIONS.EXPORT_DATA_POLICY);

  const [
    { selectedAgencyItems, progressPercent: loadingProgressPercent },
    {
      updateSelectedShareItems,
      updateSelectedShareItem,
      getData,
      exportFiles,
      deleteShareDetail,
      cancelRequests,
      resetSelectedShareItems,
    },
  ] = useSharedTabStore();

  const getSearchCriteria = (): InventorySharedSearchCriteria => {
    let criteria: InventorySharedSearchCriteria = {
      ...filters,
      listCount: rowsPerPage,
      requestedPage: page,
    };

    if (sortColumn) {
      criteria.sortColumn = sortColumn;
    }

    return criteria;
  };

  const loadSettings = async () => {
    const debounceTime = generalConfig.userInputDebounceTimeMs;
    if (debounceTime) {
      setUserInputDebounceTimeMs(debounceTime);
    }
  };

  const getDefaultFilters = (): InventorySharedSearchCriteria => ({
    listCount: rowsPerPage,
    requestedPage: 1,
    sortColumn: "AssignedLocationDisplayName",
  });

  const getDatabaseSortColumnName = (name: string) => {
    const mappedColumnNames: { [key: string]: string } = {
      assignedCompanyName: "AssignedCompanyName",
      assignedCompanyLegacyID: "AssignedCompanyLegacyID",
      assignedLocationDisplayName: "AssignedLocationDisplayName",
      assignedLocationLegacyID: "AssignedLocationLegacyID",
      assignedLocationStateAbbr: "AssignedLocationStateAbbr",
    };
    return mappedColumnNames[name] ?? name;
  };

  const performSearch = async () => {
    const criteria = getSearchCriteria();
    try {
      const records = await getData({ ...criteria });
      if (records !== undefined) {
        setData(records);
        setEmptyStateType(EmptyStateType.DEFAULT);
        if (!records?.length) {
          setEmptyStateType(EmptyStateType.NO_RESULTS);
          setPage(1);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleToggleAllRows = async () => {
    if (data) {
      updateSelectedShareItems(data);
    }
  };

  const handleToggleRow = (row: InventorySharedUI) => {
    updateSelectedShareItem(row);
  };

  const selectionConfig: SelectionConfig<InventorySharedUI> = {
    enabled: canExportShares,
    selectedRows: selectedAgencyItems?.map((d) => d.uniqueIdentifier) ?? [],
    onToggleAllRows: handleToggleAllRows,
    onToggleRow: handleToggleRow,
    isRowSelected: (row: InventorySharedUI) =>
      selectedAgencyItems?.some(
        (d) => d.uniqueIdentifier === row.uniqueIdentifier
      ),
  };

  const handleRowsPerPageChange = async (count: number) => {
    await cancelRequests();
    setRowsPerPageSetting(count);
    setRowsPerPage(count);
    setPage(1);
  };

  const handlePageChange = async (newPage: number) => {
    await cancelRequests();
    setPage(newPage);
  };

  const handleFilterChange = async (
    name: keyof InventorySharedSearchCriteria,
    value: string | null
  ) => {
    await cancelRequests();
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handleRequestSort = async (
    columnName: any,
    disableToggling: boolean = false
  ) => {
    await cancelRequests();
    let sortBy = getDatabaseSortColumnName(columnName);
    if (!disableToggling && sortColumn === sortBy) {
      sortBy = `-${sortBy}`;
    }

    setOrder(sortBy.startsWith("-") ? "desc" : "asc");
    setOrderBy(columnName.replace("-", "") as keyof InventorySharedUI);
    setSortColumn(sortBy);
  };

  const canSearch = (): boolean =>
    filters !== undefined && Object.values(filters).length > 0;

  const debounceSetReadyToSearch = useCallback(
    debounce(() => setReadyToSearch(true), userInputDebounceTimeMs),
    [userInputDebounceTimeMs]
  );

  const handleDelete = (row: ReceivingAgency) => {
    setShareToDelete(row);
  };

  const handleCancelDelete = () => {
    setShareToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!shareToDelete || !canDeleteShare) {
      return;
    }

    await deleteShareDetail(
      shareToDelete.inventoryShareDetailID,
      shareToDelete.inventoryShareID
    );
    setShareToDelete(null);
    await performSearch();
  };

  const handleExport = async (allFiles: boolean) => {
    if (!canExportShares) {
      setOpenExportDialog(false);
      return;
    }

    const exportCriteria: InventorySharedExportCriteria = {
      ExportColumns: ["AssignedLocationDisplayName", "AssignedLocationLegacyID", "SharedLocationDisplayName", "SharedLocationLegacyID"],
      HideRepeatedExportColumns: ["AssignedLocationDisplayName", "AssignedLocationLegacyID"],
      InventoryShareDetailSearchKey: filters?.assignedAgent ?? "",
      InventorySharedAssignedLocationUniqueIDLists: allFiles ? [] :
       selectedAgencyItems?.map(
        (item) => item.assignedLocationUniqueID
      )
    };

    await exportFiles(exportCriteria);
    setOpenExportDialog(false);
  };

  const handleColumnResize = () => {
    // Column resize is not required since there is only a single column
  };

  const columns: StewartTableColumn<InventorySharedUI>[] = useMemo(
    () => [
      {
        field: "assignedLocationDisplayName",
        name: "Assigned Agency",
        sortable: true,
        resizable: true,
        position: 1,
        actionComponent: () => (
          <InputSearch name="assignedAgent" onChange={handleFilterChange} />
        ),
        valueGetter: (row: InventorySharedUI) => (
          <Box
            component="span"
            sx={{ fontWeight: fontWeight.bold1, color: colors.blue15 }}
          >
            {row.assignedLocationDisplayName} ({row.assignedLocationLegacyID})
          </Box>
        ),
      },
    ],
    [filters]
  );

  const detailsViewConfig: DetailsViewConfig<InventorySharedUI> = useMemo(
    () => ({
      enabled: true,
      renderRowDetails: (row: InventorySharedUI) => (
        <DetailsContainer>
          <Typography
            sx={{
              fontSize: fontSize.large,
              fontWeight: fontWeight.bold1,
              marginBottom: gaps.small1,
            }}
          >
            Sharing Agency / Legacy ID (Location)
          </Typography>

          {row.receivingAgencies.length > 0 ? (
            row.receivingAgencies.map((agent) => (
              <DetailsRow key={agent.sharedLocationLegacyID}>
                <Box sx={{ minWidth: 240, fontWeight: fontWeight.bold1 }}>
                  {agent.sharedLocationDisplayName} (
                  {agent.sharedLocationLegacyID})
                </Box>
                {canDeleteShare && (
                  <IconButton
                  onClick={() => handleDelete(agent)}
                  aria-label="delete share"
                  size="small"
                >
                  <DeleteIcon sx={{ color: colors.blue15 }} />
                </IconButton>
                )}
              </DetailsRow>
            ))
          ) : (
            <Typography
              sx={{
                fontSize: fontSize.large,
                color: colors.grey11,
                paddingLeft: padding.xlarge21,
              }}
            >
              No specific sharing agents on file.
            </Typography>
          )}
        </DetailsContainer>
      ),
    }),
    [],
  );

  useEffect(() => {
    const init = async () => {
      await loadSettings();
      resetSelectedShareItems();
      setSettingsLoaded(true);
    };
    init();
  }, []);

  useEffect(() => {
    setFilters(getDefaultFilters());
  }, []);

  useEffect(() => {
    const getCount = async () => {
      const count = await getRowsPerPageSetting();
      setRowsPerPage(count);
    };
    getCount();
  }, [getRowsPerPageSetting]);

  useEffect(() => {
    if (canSearch()) {
      debounceSetReadyToSearch();
    }
  }, [
    filters,
    page,
    sortColumn,
    settingsLoaded,
    rowsPerPage,
  ]);

  useEffect(() => {
    if (readyToSearch) {
      const search = async () => {
        await performSearch();
        setReadyToSearch(false);
      };
      search();
    }
  }, [readyToSearch]);

  const noRowsMessage = [NoRecordsMessage];

  return (
    <Container>
      <Toolbar>
        {canExportShares && (
          <ToolbarActions>
            <Button
              disabled={!permissionCodes?.includes(PERMISSIONS.EXPORT_DATA_POLICY)}
              style={{
                opacity: 1,
              }}
              onClick={() => (selectedAgencyItems?.length === 0 ? handleExport(true) : setOpenExportDialog(true))}
            >
              <>
                Export
                <IosShareIcon className="icon rotate-90" />
              </>
            </Button>
          </ToolbarActions>
        )}
        {selectedAgencyItems?.length > 0 && (
          <SelectedHeader page={PageType.SharedPage} />
        )}
      </Toolbar>


    <StyledTableContainer width={1}>  
      <StewartTable
        cols={columns}
        rows={data ?? []}
        page={page}
        rowsPerPage={rowsPerPage}
        showActionRow={true}
        noRowsMessage={noRowsMessage}
        orderBy={orderBy}
        order={order}
        useDbPagingSorting={true}
        onRequestSort={handleRequestSort}
        onColumnResize={handleColumnResize}
        emptyStateType={emptyStateType}
        detailsViewConfig={detailsViewConfig}
        selectionConfig={selectionConfig}
        rowHeaderWidth={ROW_HEADER_WIDTH}
        loadingProgressPercent={loadingProgressPercent}
      />

      <StewartTableFooterPaginator
        totalRows={data?.[0]?.totalRows || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        totalPages={data?.[0]?.totalPages}
        disablePagination={emptyStateType === EmptyStateType.NO_RESULTS}
      />
    </StyledTableContainer>
      <FileExportDialog
        open={openExportDialog}
        onCancel={() => setOpenExportDialog(false)}
        onExport={handleExport}
        showSelectionPrompt={selectedAgencyItems?.some((s) => s)}
        selectionPrompt={"Include only the selected agencies or all agencies?"}
        icon={IosShareIcon}
        fileSelectionChoices={EXPORT_AGENCY_SELECTION_CHOICES}
      />

      <ConfirmationDialog
        isOpen={!!shareToDelete}
        confirmationMessage={`Are you sure you want to delete the share for ${shareToDelete?.sharedLocationDisplayName}?`}
        onYes={handleConfirmDelete}
        onNo={handleCancelDelete}
        cancelActionButton
        onCancel={handleCancelDelete}
      />
    </Container>
  );
};

export default InventoryShareShared;
